import createClient, { type Middleware } from 'openapi-fetch'
import { captureSafeMessage } from '@/lib/sentry-instrument'
import type { paths } from './schema'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080'
const REQUEST_TIMEOUT_MS = 30_000

export class AuthError extends Error {
  status: number
  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'AuthError'
    this.status = status
  }
}

/**
 * Wraps openapi-fetch's parsed JSON error body in a real Error subclass so
 * that callers (and React error renderers) get a human-readable `.message`
 * instead of stringifying a plain object into `[object Object]`.
 */
export class ApiError extends Error {
  body: unknown
  /**
   * HTTP status code of the failing response. Optional so existing call
   * sites that just pass the parsed body (most of `queries.ts`) keep
   * working — newer hooks that want to distinguish 429/409/422 from a
   * generic 4xx can pass it explicitly.
   */
  status?: number
  constructor(body: unknown, status?: number) {
    super(formatErrorBody(body))
    this.name = 'ApiError'
    this.body = body
    this.status = status
  }
}

/**
 * 409 from PATCH endpoints when a caller's base revision no longer matches
 * the resource head and ``force_overwrite=false``. The body holds the
 * conflicting fields so the UI can render a "your save would overwrite
 * N agent edits" dialog before retrying.
 *
 * We carve this out into its own subclass so the Save mutation handler
 * can ``instanceof``-test once instead of poking at ``error.body`` shape.
 */
export class RevisionConflictError extends ApiError {
  constructor(body: unknown) {
    super(body)
    this.name = 'RevisionConflictError'
  }

  static isConflictBody(body: unknown): boolean {
    return (
      typeof body === 'object' &&
      body !== null &&
      'conflicting_fields' in body &&
      'current_revision' in body
    )
  }
}

function formatErrorBody(body: unknown): string {
  if (body instanceof Error) return body.message
  if (typeof body === 'string') return body
  if (typeof body === 'object' && body !== null && 'detail' in body) {
    const detail = (body as { detail: unknown }).detail
    if (typeof detail === 'string') return detail
    if (typeof detail === 'object' && detail !== null && 'message' in detail) {
      const message = (detail as { message?: unknown }).message
      const checks = (detail as { checks?: unknown }).checks
      const checkText = Array.isArray(checks) ? checks.filter((item) => typeof item === 'string').join(' ') : ''
      return [typeof message === 'string' ? message : null, checkText].filter(Boolean).join(' ')
    }
    try {
      return JSON.stringify(detail)
    } catch {
      return String(detail)
    }
  }
  try {
    return JSON.stringify(body)
  } catch {
    return 'Unknown error'
  }
}

/**
 * Token getter installed by TokenBridge after Logto has booted. We keep
 * a module-level pointer here (instead of resolving it lazily inside fetch)
 * so the openapi-fetch client can stay a stable singleton.
 */
let getAccessToken: (() => Promise<string>) | null = null
let clearAccessTokenCache: (() => Promise<void>) | null = null
let onSessionExpired: (() => void) | null = null

export function installAccessTokenGetter(fn: () => Promise<string>): void {
  getAccessToken = fn
}

/**
 * Async accessor for the Logto access token, exposed for non-REST callers
 * (the long-lived connection hook) that need to attach the bearer themselves
 * since browsers can't set Authorization on a WS upgrade. Returns an empty
 * string when the token bridge hasn't been installed yet (pre-login) or
 * Logto's own ``getAccessToken`` rejects — callers should treat empty as
 * "no token" and surface a session-expired UI.
 */
export async function currentAccessToken(): Promise<string> {
  if (!getAccessToken) return ''
  try {
    return (await getAccessToken()) || ''
  } catch (err) {
    console.warn('currentAccessToken: getAccessToken failed', err)
    return ''
  }
}

/**
 * Bridge to Logto's `clearAccessToken()` (or any equivalent that drops the
 * locally cached access token). We call this before forcing a refresh after a
 * 401, so the next `getAccessToken()` actually round-trips to the IdP via the
 * refresh token instead of handing back the same expired bearer.
 */
export function installClearAccessToken(fn: () => Promise<void>): void {
  clearAccessTokenCache = fn
}

/**
 * Called when authFetch has exhausted its single refresh-and-replay attempt
 * and the session is genuinely dead (no refresh token, refresh rejected, or
 * the backend still 401s with the freshly-minted token). The handler is
 * responsible for navigating the user to /login or rendering a
 * session-expired UX. Test-only callers can leave this unset.
 */
export function installSessionExpiredHandler(fn: () => void): void {
  onSessionExpired = fn
}

/**
 * Test/reset hook — wipes the module-level handlers between tests so a stale
 * navigate-to-login mock from one test can't fire inside an unrelated one.
 */
export function __resetAuthBridgeForTests(): void {
  getAccessToken = null
  clearAccessTokenCache = null
  onSessionExpired = null
  pendingRefresh = null
}

/**
 * Coalesces concurrent refresh attempts. Multiple in-flight requests that all
 * 401 at once should share a SINGLE refresh — otherwise we'd hammer Logto's
 * /token endpoint with N parallel refresh_token grants, get rate-limited, and
 * stall the page rehydrate that mobile-after-sleep absolutely depends on.
 *
 * The promise is cleared as soon as it resolves so a *future* 401 (after the
 * new token has been used and itself expires) can start its own refresh.
 */
let pendingRefresh: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  if (pendingRefresh) return pendingRefresh
  const refresh = (async () => {
    if (clearAccessTokenCache) {
      try {
        await clearAccessTokenCache()
      } catch (err) {
        // clearAccessToken failure is non-fatal — the next getAccessToken
        // call may still produce a working token. Log so we notice the
        // pattern but don't abort the refresh attempt.
        console.warn('authFetch: clearAccessToken failed', err)
      }
    }
    if (!getAccessToken) return ''
    try {
      const next = await getAccessToken()
      return next ?? ''
    } catch (err) {
      // Logto throws when refresh fails (revoked refresh_token, network
      // error, etc.). Treat this as a dead session — the caller will surface
      // session-expired UX.
      console.warn('authFetch: forced refresh failed', err)
      captureSafeMessage('auth.refresh_failed', 'warning', {
        operation: 'logto_refresh',
        exc_type: err instanceof Error ? err.name : typeof err,
      })
      return ''
    }
  })()
  pendingRefresh = refresh
  refresh.finally(() => {
    if (pendingRefresh === refresh) pendingRefresh = null
  })
  return refresh
}

function buildHeaders(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string | null,
): Headers {
  // openapi-fetch invokes this fetcher as `fetch(request, undefined)` — the
  // body and Content-Type live on the Request, not in init. If we built
  // `new Headers(init?.headers)` and passed it back, the WhatWG Fetch spec
  // says init.headers REPLACES Request.headers, dropping Content-Type and
  // making FastAPI reject the body as `model_attributes_type`.
  //
  // So: start from the Request's own headers when input is a Request,
  // overlay any caller-provided init.headers on top, then set Authorization.
  // When called with a string/URL input (e.g. subscribeSSE), fall back to
  // init?.headers as before.
  const headers = new Headers(
    input instanceof Request ? input.headers : init?.headers,
  )
  if (input instanceof Request && init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value))
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return headers
}

async function parseAuthDetail(response: Response): Promise<string | undefined> {
  const body = await response
    .clone()
    .json()
    .catch(() => ({}) as Record<string, unknown>)
  const detail = (body as { detail?: unknown }).detail
  return typeof detail === 'string' ? detail : undefined
}

/**
 * window.fetch wrapper that:
 *  - injects `Authorization: Bearer <logto_access_token>`
 *  - aborts after REQUEST_TIMEOUT_MS
 *  - on 401, immediately attempts ONE coalesced refresh and replays the
 *    request with the new bearer (so a stale-cache 401 looks invisible to
 *    the caller instead of percolating up as a hard error)
 *  - if the replay still 401s — or the refresh itself failed — throws
 *    AuthError so React Query skips its retry/backoff and the
 *    session-expired handler can route to /login
 *  - throws AuthError on 403 immediately (refresh can't fix forbidden)
 *
 * Auth-class errors (AuthError) MUST never be retried by generic
 * retry-with-backoff loops. The QueryClient's retry predicate excludes them;
 * this fetcher does the only legitimate refresh+replay, exactly once.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  // If the caller passed a Request with a body, the body stream is consumed
  // by the first fetch attempt. Clone now so the replay can re-send it.
  const replayableInput =
    input instanceof Request ? input.clone() : input

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const firstToken = await acquireToken()
    let response = await sendWithToken(input, init, firstToken, controller.signal)

    if (response.status === 403) {
      const detail = await parseAuthDetail(response)
      throw new AuthError(403, detail || 'Forbidden')
    }
    if (response.status !== 401) {
      return response
    }

    // 401 — drop the cached bearer and force a refresh. Concurrent 401s
    // share the same in-flight refresh promise.
    const refreshed = await refreshAccessToken()
    if (!refreshed || refreshed === firstToken) {
      // Refresh produced nothing new — the session is dead. Surface the
      // session-expired UX once and propagate AuthError so React Query
      // doesn't retry with backoff.
      const detail = await parseAuthDetail(response)
      onSessionExpired?.()
      throw new AuthError(401, detail || 'Session expired')
    }

    response = await sendWithToken(
      replayableInput,
      init,
      refreshed,
      controller.signal,
    )
    if (response.status === 403) {
      // The refreshed token authenticated successfully but the user is not
      // authorized for this resource. Surface AuthError(403) so the UI can
      // render a forbidden/no-access state — DO NOT route to the
      // session-expired login flow; the session is healthy.
      const detail = await parseAuthDetail(response)
      throw new AuthError(403, detail || 'Forbidden')
    }
    if (response.status === 401) {
      const detail = await parseAuthDetail(response)
      onSessionExpired?.()
      throw new AuthError(401, detail || 'Session expired')
    }
    return response
  } catch (error) {
    if (error instanceof AuthError) throw error
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS}ms`)
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

async function acquireToken(): Promise<string | null> {
  if (!getAccessToken) return null
  try {
    return (await getAccessToken()) || null
  } catch (error) {
    // Logto throws if there is no session — that's fine, we'll send the
    // request without a token and let the backend respond with 401.
    console.warn('authFetch: token acquisition failed', error)
    captureSafeMessage('auth.token_acquire_failed', 'warning', {
      operation: 'logto_get_access_token',
      exc_type: error instanceof Error ? error.name : typeof error,
    })
    return null
  }
}

async function sendWithToken(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  token: string | null,
  defaultSignal: AbortSignal,
): Promise<Response> {
  const headers = buildHeaders(input, init, token)
  return fetch(input, {
    ...init,
    headers,
    signal: init?.signal ?? defaultSignal,
  })
}

// Middleware kept exported in case downstream apps want to add tracing or
// custom headers on top of authFetch without rebuilding the client.
export const authMiddleware: Middleware = {
  onRequest: async ({ request }) => {
    if (getAccessToken) {
      try {
        const token = await getAccessToken()
        if (token) request.headers.set('Authorization', `Bearer ${token}`)
      } catch (err) {
        console.warn('authMiddleware: token acquisition failed', err)
      }
    }
    return request
  },
}

/**
 * The single API client. Use it from queries.ts / TanStack Query hooks.
 *
 *   const { data, error } = await apiClient.GET('/dashboard/me')
 *
 * After running scripts/codegen.sh, all paths/params/responses are typed.
 */
export const apiClient = createClient<paths>({
  baseUrl: API_BASE_URL,
  fetch: authFetch,
})

export type { paths }
